import { Component, OnInit } from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { ConceptInvoicingTypeEnums } from 'src/app/enum/concept-invoicing-enums';
import { StepPanelEnums } from 'src/app/enum/step-panel-enums';
import { InvoicingModelDetailModel } from 'src/app/models/invoicing/invoicing-model-detail.model';
import { InvoicingModelModel } from 'src/app/models/invoicing/invoicing-model.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { InvoicingModelDetailService } from 'src/app/services/invoicing/invoicing-model-detail.service';
import { InvoicingModelService } from 'src/app/services/invoicing/invoicing-model.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-copy-invoicing-model',
  templateUrl: './copy-invoicing-model.component.html',
  styleUrls: ['./copy-invoicing-model.component.scss'],
  standalone: false
})
export class CopyInvoicingModelComponent implements OnInit {

  ascendantInvoicingModel: boolean = true;
  ascendantInvoicingModelDetail: boolean = true;
  columnSortInvoicingModel: string | null = null;
  columnSortInvoicingModelDetail: string | null = null;
  currentStep: number = 1;
  conceptInvoicingType: number = 1;
  dataInvoicingModel: PagedResponseModel<InvoicingModelModel> =
    new PagedResponseModel<InvoicingModelModel>();
  dataInvoicingModelDetail: PagedResponseModel<InvoicingModelDetailModel> =
    new PagedResponseModel<InvoicingModelDetailModel>();
  filtersInvoicingModel: ColumnResponseModel[] = [];
  filtersInvoicingModelDetail: ColumnResponseModel[] = [];
  headerInvoicingModel: ColumnResponseModel[] = [];
  headerInvoicingModelDetail: ColumnResponseModel[] = [];
  invoicingModelId: number = 0;
  invoicingModelDetailId: number = 0;
  limitInvoicingModel: number = 10;
  limitInvoicingModelDetail: number = 10;
  loading: boolean = false;
  readonly stepPanel = StepPanelEnums;
  invoicingModelDetailModel: InvoicingModelDetailModel = new InvoicingModelDetailModel();
  pageInvoicingModel: number = 1;
  pageInvoicingModelDetail: number = 1;
  selectedInvoicingModel: InvoicingModelModel | null = null;
  subscription: Subscription;

  constructor(public config: DynamicDialogConfig,
    private invoicingModelService: InvoicingModelService,
    private invoicingModelDetailService: InvoicingModelDetailService,
    private toastHandlerService: ToastHandlerService
  ) { }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.invoicingModelDetailModel = inputs.invoicingModelDetailModel;
      this.conceptInvoicingType = inputs.conceptInvoicingType;
    });
    this.loading = true;
    await this.getDataForStep(StepPanelEnums.PanelOne);
    setTimeout(() => this.loading = false, 300);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async getColumnsInvoicingModel() {
    try {
      if (this.headerInvoicingModel.length === 0) {
        const response = await firstValueFrom(this.invoicingModelService.getColumnsInvoicingModel());
        this.headerInvoicingModel = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para seleccionar modelos.', error);
    }
  }

  async getColumnsInvoicingModelDetail() {
    try {
      if (this.headerInvoicingModelDetail.length === 0) {
        let response;
        if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
          response = await firstValueFrom(this.invoicingModelDetailService.getColumnsPurchaseModelDetailAsync());
        } else {
          response = await firstValueFrom(this.invoicingModelDetailService.getColumnsInvoicingModelDetailAsync());
        }
        this.headerInvoicingModelDetail = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para seleccionar conceptos del modelo.', error);
    }
  }

  async getDataForStep(step: StepPanelEnums) {
    try {
      const actions: Record<number, () => Promise<any>> = {
        [StepPanelEnums.PanelOne]: () => Promise.all([
          this.getColumnsInvoicingModel(),
          this.getInvoicingModelsForSelectionAsync()
        ]),
        [StepPanelEnums.PanelTwo]: () => Promise.all([
          this.getColumnsInvoicingModelDetail(),
          this.getInvoicingModelDetailForSelectionAsync()
        ])
      };
      if (actions[step]) {
        await actions[step]();
      }
    } catch (error) {
      console.error(`Error cargando datos del ${StepPanelEnums[step]}:`, error);
    }
  }

  async getInvoicingModelsForSelectionAsync() {
    try {
      let response;
      if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
        response = await firstValueFrom(this.invoicingModelService.getPurchaseModelsForSelectionAsync(this.pageInvoicingModel, this.limitInvoicingModel,
          this.columnSortInvoicingModel, this.ascendantInvoicingModel, this.filtersInvoicingModel));
      } else {
        response = await firstValueFrom(this.invoicingModelService.getInvoicingModelsForSelectionAsync(this.pageInvoicingModel, this.limitInvoicingModel,
          this.columnSortInvoicingModel, this.ascendantInvoicingModel, this.filtersInvoicingModel));
      }
      this.dataInvoicingModel = response;
    } catch (error) {
      console.error('Error al obtener los modelos.', error);
    }
  }

  async getInvoicingModelDetailForSelectionAsync() {
    try {
      let response;
      if (this.conceptInvoicingType === ConceptInvoicingTypeEnums.compra) {
        response = await firstValueFrom(this.invoicingModelDetailService.getPurchaseModelDetailByIdForSelectionAsync(this.invoicingModelId,
          this.pageInvoicingModelDetail, this.limitInvoicingModelDetail, this.columnSortInvoicingModelDetail, this.ascendantInvoicingModelDetail,
          this.filtersInvoicingModelDetail));
      } else {
        response = await firstValueFrom(this.invoicingModelDetailService.getInvoicingModelDetailByIdForSelectionAsync(this.invoicingModelId,
          this.pageInvoicingModelDetail, this.limitInvoicingModelDetail, this.columnSortInvoicingModelDetail, this.ascendantInvoicingModelDetail,
          this.filtersInvoicingModelDetail));
      }
      this.dataInvoicingModelDetail = response;
    } catch (error) {
      console.error('Error al obtener los detalles del modelo.', error);
    }
  }

  async goToStep(step: StepPanelEnums, activateCallback: (step: number) => void) {
    if (step > StepPanelEnums.PanelOne && this.invoicingModelId === 0) {
      this.toastHandlerService.showToast('warning', 'Por favor, seleccione un modelo de facturación válido.');
      return;
    }
    if (step === StepPanelEnums.PanelOne) {
      this.invoicingModelId = 0;
    }
    await this.getDataForStep(step);
    this.currentStep = step;
    activateCallback(step);
  }

  async onFilterChange(event: { filters: any[] }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    const cleanFilters = this.removeDuplicates(filters);
    switch (this.currentStep) {
      case StepPanelEnums.PanelOne: {
        this.filtersInvoicingModel = cleanFilters;
        this.filtersInvoicingModel.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelOne));
        break;
      }
      case StepPanelEnums.PanelTwo: {
        this.filtersInvoicingModelDetail = cleanFilters;
        this.filtersInvoicingModelDetail.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelTwo));
        break;
      }
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    switch (this.currentStep) {
      case StepPanelEnums.PanelOne: {
        this.pageInvoicingModel = event.page;
        this.limitInvoicingModel = event.pageSize;
        this.columnSortInvoicingModel = event.sortField || null;
        this.ascendantInvoicingModel = event.sortOrder === 1;
        await this.getInvoicingModelsForSelectionAsync()
        break;
      }
      case StepPanelEnums.PanelTwo: {
        this.pageInvoicingModelDetail = event.page;
        this.limitInvoicingModelDetail = event.pageSize;
        this.columnSortInvoicingModelDetail = event.sortField || null;
        this.ascendantInvoicingModelDetail = event.sortOrder === 1;
        await this.getInvoicingModelDetailForSelectionAsync()
        break;
      }
    }
  }

  processFilter(filter: any, step: StepPanelEnums) {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    const isInvalid = valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0;
    if (isInvalid) {
      if (step == StepPanelEnums.PanelOne) {
        this.filtersInvoicingModel = this.filtersInvoicingModel.filter(f => f.propertyName !== propertyName);
      }
      else if (step == StepPanelEnums.PanelTwo) {
        this.filtersInvoicingModelDetail = this.filtersInvoicingModelDetail.filter(f => f.propertyName !== propertyName);
      }
    } else {
      const list = step === StepPanelEnums.PanelOne ? this.filtersInvoicingModel : this.filtersInvoicingModelDetail;
      const existingFilter = list.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async selectionChange({ selectedItem }: { selectedItem?: InvoicingModelModel }) {
    const { invoicingModelId, } = selectedItem ?? {};
    this.invoicingModelId = invoicingModelId ?? 0;
    this.selectedInvoicingModel = selectedItem ?? null;
  }
}